/**
 * Sortable, sticky column header for any table in the app — drill-down modals included.
 *
 * Lived under bab-report/ first and was never BAB-specific; it moved here when the VAT
 * drill-down needed the same thing, rather than becoming a second implementation. The BAB
 * Process App still duplicates it per modal — that is the thing not to copy.
 *
 * The cell carries `sticky top-0` itself, so a table only needs a scroll container with a
 * bounded height for the header to stay put (see feedback_sticky_header_needs_bounded_scroll).
 *
 * Optionally resizable: pass `onResizeStart` / `onResetWidth` and the cell grows a drag strip
 * on its right edge. Sorting and resizing share one cell, so they also share the guard that
 * keeps a finished drag from toggling the sort (`suppressClick` in useResizableColumns).
 *
 * Optionally filterable: pass `filter` (a `<ColumnFilter>` element) and it renders next to the
 * label. The filter owns its own click handling and stops propagation itself, so it never
 * toggles the sort — same pattern as the resize handle.
 */
import type { ReactNode } from 'react';
import { ChevronUp, ChevronDown } from 'lucide-react';
import { ColumnResizeHandle } from './ColumnResizeHandle';

export type SortDirection = 'asc' | 'desc';

export interface SortState<K extends string> {
    key: K | null;
    direction: SortDirection;
}

/** New key → text sorts ascending, numbers descending; same key → flip. */
export function nextSort<K extends string>(current: SortState<K>, key: K, numeric: boolean): SortState<K> {
    if (current.key === key) {
        return { key, direction: current.direction === 'asc' ? 'desc' : 'asc' };
    }
    return { key, direction: numeric ? 'desc' : 'asc' };
}

interface Props<K extends string> {
    label: string;
    sortKey: K;
    sort: SortState<K>;
    onSort: (key: K) => void;
    align?: 'left' | 'right';
    className?: string;
    /** Resizing — all three go together, or none of them. */
    onResizeStart?: (clientX: number) => void;
    onResetWidth?: () => void;
    isResizing?: boolean;
    /** A `<ColumnFilter>` (or anything else) rendered next to the label. */
    filter?: ReactNode;
    /** The full name on hover where the label may be cut ("Open Gross …") — strategic KPIs audit S2-11. */
    title?: string;
}

export function SortableHeader<K extends string>({
    label, sortKey, sort, onSort, align = 'left', className = '',
    onResizeStart, onResetWidth, isResizing = false, filter, title,
}: Props<K>) {
    const active = sort.key === sortKey;
    const iconClass = (dir: SortDirection) =>
        active && sort.direction === dir ? 'text-gray-900' : 'text-gray-300';

    return (
        <th
            className={`sticky top-0 z-10 bg-gray-50 px-3 py-2 text-xs font-medium text-gray-500 select-none hover:bg-gray-100 ${onResizeStart || filter ? 'relative' : ''} ${align === 'right' ? 'text-right' : 'text-left'} ${className}`}
            aria-sort={active ? (sort.direction === 'asc' ? 'ascending' : 'descending') : 'none'}
        >
            <span className={`flex w-full items-center gap-1 ${align === 'right' ? 'flex-row-reverse' : ''}`}>
                {/* A button, so the header sorts by keyboard too (design audit 2026-10-07, S1-01).
                    It was an `onClick` on the <th>, reachable by mouse only. */}
                <button
                    type="button"
                    onClick={() => onSort(sortKey)}
                    title={title}
                    className={`flex min-w-0 flex-1 items-center gap-1 rounded focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-500 ${align === 'right' ? 'flex-row-reverse' : ''}`}
                >
                    <span className="truncate">{label}</span>
                    <span className="inline-flex flex-col -space-y-1 shrink-0" aria-hidden="true">
                        <ChevronUp size={10} className={iconClass('asc')} />
                        <ChevronDown size={10} className={iconClass('desc')} />
                    </span>
                </button>
                {filter}
            </span>
            {onResizeStart && onResetWidth && (
                <ColumnResizeHandle
                    label={label}
                    active={isResizing}
                    onStart={onResizeStart}
                    onReset={onResetWidth}
                />
            )}
        </th>
    );
}
